import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import SEOHead from "@/components/SEOHead";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Check, ArrowRight, Loader2, X } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useAuth } from "@/hooks/useAuth";
import { useSubscription } from "@/hooks/useSubscription";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { SUBSCRIPTION_TIERS } from "@/lib/subscriptions";

type PlanKey = "nazionaleBase" | "nazionaleProfessional" | "europaBase" | "europaProfessional";

export default function Pricing() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { productId: currentProductId } = useSubscription();
  const { toast } = useToast();
  const [loadingPlan, setLoadingPlan] = useState<PlanKey | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // ✅ Stable canonical base + avoid www mismatch
  const siteUrl =
    typeof window !== "undefined" && window.location?.origin ? window.location.origin : "https://tedmonitor.eu";

  // ✅ Handle checkout cancelled parameter (safe mutation)
  useEffect(() => {
    if (searchParams.get("checkout") === "cancelled") {
      toast({
        title: t("pricing.checkoutCancelled", { defaultValue: "Checkout annullato" }),
        description: t("pricing.checkoutCancelledDescription", {
          defaultValue: "Il processo di pagamento è stato annullato. Puoi riprovare quando vuoi.",
        }),
      });

      const next = new URLSearchParams(searchParams);
      next.delete("checkout");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams, toast, t]);

  // ✅ Plans derived from tiers (keep memo stable)
  const plans = useMemo(
    () => [
      {
        key: "nazionaleBase" as const,
        price: SUBSCRIPTION_TIERS.nazionaleBase.price,
        priceId: SUBSCRIPTION_TIERS.nazionaleBase.priceId,
        productId: SUBSCRIPTION_TIERS.nazionaleBase.productId,
        highlight: false,
      },
      {
        key: "nazionaleProfessional" as const,
        price: SUBSCRIPTION_TIERS.nazionaleProfessional.price,
        priceId: SUBSCRIPTION_TIERS.nazionaleProfessional.priceId,
        productId: SUBSCRIPTION_TIERS.nazionaleProfessional.productId,
        highlight: true,
      },
      {
        key: "europaBase" as const,
        price: SUBSCRIPTION_TIERS.europaBase.price,
        priceId: SUBSCRIPTION_TIERS.europaBase.priceId,
        productId: SUBSCRIPTION_TIERS.europaBase.productId,
        highlight: false,
      },
      {
        key: "europaProfessional" as const,
        price: SUBSCRIPTION_TIERS.europaProfessional.price,
        priceId: SUBSCRIPTION_TIERS.europaProfessional.priceId,
        productId: SUBSCRIPTION_TIERS.europaProfessional.productId,
        highlight: false,
      },
    ],
    [],
  );

  const handleSubscribe = async (plan: (typeof plans)[number]) => {
    if (!user) {
      navigate(`/auth?plan=${plan.key}`);
      return;
    }

    if (currentProductId === plan.productId) {
      toast({
        title: t("pricing.currentPlan", { defaultValue: "Piano attuale" }),
        description: t("pricing.alreadySubscribed", { defaultValue: "Sei già abbonato a questo piano" }),
      });
      return;
    }

    setLoadingPlan(plan.key);

    try {
      const { data, error } = await supabase.functions.invoke("create-checkout", {
        body: { priceId: plan.priceId, country: "IT" },
      });

      if (error) throw error;
      if (data?.url) window.location.href = data.url; // ✅ better than window.open for checkout flows
    } catch (err: any) {
      console.error("Checkout error:", err);
      toast({
        variant: "destructive",
        title: t("common.error", { defaultValue: "Errore" }),
        description: err?.message || t("pricing.checkoutError", { defaultValue: "Errore durante il checkout" }),
      });
    } finally {
      setLoadingPlan(null);
    }
  };

  // ✅ Avoid double “- TED Monitor” (SEOHead already appends brand in your component)
  const metaTitle = t("pricing.metaTitle", { defaultValue: "Plans & Pricing" });
  const metaDescription = t("pricing.metaDescription", {
    defaultValue:
      "Choose a plan for European public procurement monitoring on TED: alerts, advanced search, and multi-country coverage. Transparent monthly pricing.",
  });

  const language = (i18n.language?.split("-")[0] || "en").toLowerCase();
  const currency = "EUR";

  // ✅ Pricing JSON-LD (OfferCatalog)
  const pricingStructuredData = useMemo(() => {
    const offers = plans.map((p) => ({
      "@type": "Offer",
      name: t(`pricing.plans.${p.key}.name`, { defaultValue: p.key }),
      price: String(p.price),
      priceCurrency: currency,
      url: `${siteUrl}/pricing`,
      availability: "https://schema.org/InStock",
      category: "subscription",
    }));

    return {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: metaTitle,
      description: metaDescription,
      url: `${siteUrl}/pricing`,
      inLanguage: language,
      mainEntity: {
        "@type": "OfferCatalog",
        name: "TED Monitor Pricing",
        itemListElement: offers,
      },
      isPartOf: {
        "@type": "WebSite",
        name: "TED Monitor",
        url: siteUrl,
      },
      about: {
        "@type": "SoftwareApplication",
        name: "TED Monitor",
        applicationCategory: "BusinessApplication",
        operatingSystem: "Web",
      },
    };
  }, [plans, siteUrl, metaTitle, metaDescription, language, currency, t]);

  return (
    <>
      {/* ✅ Alternates EN/IT + canonical handled inside SEOHead */}
      <SEOHead
        title={metaTitle}
        description={metaDescription}
        path="/pricing"
        alternates={[
          { lang: "en", href: "https://tedmonitor.eu/pricing" },
          { lang: "it", href: "https://tedmonitor.eu/it/prezzi" },
          { lang: "x-default", href: "https://tedmonitor.eu/pricing" },
        ]}
      />

      <Helmet>
        <script type="application/ld+json">{JSON.stringify(pricingStructuredData)}</script>
      </Helmet>

      <div className="min-h-screen bg-background">
        {/* Header */}
        <header className="border-b bg-card/80 backdrop-blur-md sticky top-0 z-40">
          <div className="container mx-auto px-4 py-3 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center overflow-hidden shadow-sm">
                <img
                  src="/tedmonitor-logo-mark.png"
                  alt="TED Monitor"
                  width={20}
                  height={20}
                  loading="eager"
                  className="w-5 h-5 object-contain"
                />
              </div>
              <span className="text-sm font-semibold text-foreground">TED Monitor</span>
            </Link>

            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => navigate("/")}>
                {t("common.back", { defaultValue: "Indietro" })}
              </Button>
              <Button size="sm" className="h-8 text-xs" onClick={() => navigate("/auth")}>
                {t("auth.login", { defaultValue: "Accedi" })}
              </Button>
            </div>
          </div>
        </header>

        <main className="container mx-auto px-4 py-10">
          <div className="max-w-5xl mx-auto">
            {/* Headline */}
            <div className="text-center mb-8">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground">{t("pricing.title")}</h1>
              <p className="mt-2 text-sm text-muted-foreground">{t("pricing.subtitle")}</p>
              <p className="mt-3 text-xs text-muted-foreground">
                {t("pricing.microcopy", {
                  defaultValue:
                    "Transparent monthly pricing. Upgrade any time. Start with the plan that matches your monitoring scope.",
                })}
              </p>
            </div>

            {/* Cards */}
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              {plans.map((p) => {
                const name = t(`pricing.plans.${p.key}.name`);
                const users = t(`pricing.plans.${p.key}.users`);
                const description = t(`pricing.plans.${p.key}.description`);
                const idealFor = t(`pricing.plans.${p.key}.idealFor`);

                const featuresObj = t(`pricing.plans.${p.key}.features`, {
                  returnObjects: true,
                  defaultValue: {},
                }) as Record<string, string>;
                const featureKeys = Object.keys(featuresObj || {});

                return (
                  <Card
                    key={p.key}
                    className={[
                      "bg-card/80 backdrop-blur-sm",
                      p.highlight ? "border-primary shadow-md shadow-primary/15" : "border-border",
                    ].join(" ")}
                  >
                    <CardContent className="p-5 flex flex-col h-full">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <div className="text-sm font-semibold text-foreground">{name}</div>
                          <div className="mt-1 text-xs text-muted-foreground">{users}</div>
                        </div>
                        {p.highlight && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20">
                            {t("pricing.recommended", { defaultValue: "Recommended" })}
                          </span>
                        )}
                      </div>

                      <div className="mt-4">
                        <div className="flex items-end gap-1">
                          <div className="text-2xl font-bold text-foreground">€{p.price}</div>
                          <div className="text-xs text-muted-foreground">/{t("pricing.monthly")}</div>
                        </div>
                        <div className="mt-1 text-[10px] text-muted-foreground">
                          {t("pricing.autoRenewal", { defaultValue: "Rinnovo automatico mensile" })}
                        </div>
                        <div className="mt-2 text-xs text-muted-foreground leading-relaxed">{description}</div>
                      </div>

                      <div className="mt-4">
                        <div className="text-[11px] font-medium text-foreground">{t("pricing.features")}</div>

                        <ul className="mt-2 space-y-1.5">
                          {featureKeys.map((fk) => (
                            <li key={fk} className="flex items-start gap-2 text-[11px] text-muted-foreground">
                              <Check className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                              <span>{featuresObj[fk]}</span>
                            </li>
                          ))}
                        </ul>

                        <div className="mt-4 text-[11px] text-muted-foreground">
                          <span className="font-medium text-foreground">{t("pricing.idealFor")}:</span> {idealFor}
                        </div>
                      </div>

                      <div className="mt-5 pt-4 border-t border-border/60">
                        {currentProductId === p.productId ? (
                          <Button className="w-full h-9 text-sm" variant="secondary" disabled>
                            {t("pricing.currentPlan")}
                          </Button>
                        ) : (
                          <Button
                            className="w-full h-9 text-sm group"
                            variant={p.highlight ? "default" : "outline"}
                            onClick={() => handleSubscribe(p)}
                            disabled={loadingPlan === p.key}
                          >
                            {loadingPlan === p.key ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                {t("pricing.subscribe")}
                                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-0.5 transition-transform" />
                              </>
                            )}
                          </Button>
                        )}

                        <div className="mt-2 text-[10px] text-muted-foreground text-center">
                          {t("pricing.trialNote", {
                            defaultValue:
                              "Free access available (3 daily searches). Subscriptions unlock extended monitoring and exports.",
                          })}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Comparison Table */}
            <div className="mt-12">
              <h2 className="text-xl font-bold text-foreground text-center mb-6">
                {t("pricing.comparison.title", { defaultValue: "Confronta i Piani" })}
              </h2>

              <div className="overflow-x-auto">
                <Table className="w-full">
                  <TableHeader>
                    <TableRow className="border-border/50">
                      <TableHead className="text-xs font-medium text-muted-foreground w-[180px]">
                        {t("pricing.comparison.feature", { defaultValue: "Funzionalità" })}
                      </TableHead>
                      <TableHead className="text-center text-xs font-medium text-muted-foreground">
                        {t("pricing.comparison.free", { defaultValue: "Free" })}
                      </TableHead>
                      {plans.map((p) => (
                        <TableHead
                          key={p.key}
                          className={`text-center text-xs font-medium ${p.highlight ? "text-primary" : "text-foreground"}`}
                        >
                          {t(`pricing.plans.${p.key}.name`)}
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>

                  <TableBody>
                    <TableRow className="border-border/30">
                      <TableCell className="text-xs text-muted-foreground font-medium">
                        {t("pricing.comparison.dailySearches", { defaultValue: "Ricerche giornaliere" })}
                      </TableCell>
                      <TableCell className="text-center text-xs">3</TableCell>
                      {plans.map((p) => (
                        <TableCell key={p.key} className="text-center text-xs">
                          {t("common.unlimited", { defaultValue: "Illimitate" })}
                        </TableCell>
                      ))}
                    </TableRow>

                    <TableRow className="border-border/30">
                      <TableCell className="text-xs text-muted-foreground font-medium">
                        {t("pricing.comparison.resultsPerSearch", { defaultValue: "Risultati per ricerca" })}
                      </TableCell>
                      <TableCell className="text-center text-xs">3</TableCell>
                      {plans.map((p) => (
                        <TableCell key={p.key} className="text-center text-xs">
                          {t("common.unlimited", { defaultValue: "Illimitati" })}
                        </TableCell>
                      ))}
                    </TableRow>

                    <TableRow className="border-border/30">
                      <TableCell className="text-xs text-muted-foreground font-medium">
                        {t("pricing.comparison.cpvCodes", { defaultValue: "Codici CPV" })}
                      </TableCell>
                      <TableCell className="text-center text-xs">1</TableCell>
                      {plans.map((p) => (
                        <TableCell key={p.key} className="text-center text-xs">
                          {t("common.unlimited", { defaultValue: "Illimitati" })}
                        </TableCell>
                      ))}
                    </TableRow>

                    <TableRow className="border-border/30">
                      <TableCell className="text-xs text-muted-foreground font-medium">
                        {t("pricing.comparison.valueFilters", { defaultValue: "Filtri per valore" })}
                      </TableCell>
                      <TableCell className="text-center">
                        <X className="w-4 h-4 text-muted-foreground/50 mx-auto" />
                      </TableCell>
                      {plans.map((p) => (
                        <TableCell key={p.key} className="text-center">
                          <Check className="w-4 h-4 text-primary mx-auto" />
                        </TableCell>
                      ))}
                    </TableRow>

                    <TableRow className="border-border/30">
                      <TableCell className="text-xs text-muted-foreground font-medium">
                        {t("pricing.comparison.resultHistory", { defaultValue: "Storico risultati" })}
                      </TableCell>
                      <TableCell className="text-center text-xs text-muted-foreground">
                        {t("pricing.comparison.noHistory", { defaultValue: "Nessuno" })}
                      </TableCell>
                      {/* keep your intended mapping; values can be later driven from tiers */}
                      <TableCell className="text-center text-xs">
                        {t("pricing.comparison.days30", { defaultValue: "30 giorni" })}
                      </TableCell>
                      <TableCell className="text-center text-xs">
                        {t("pricing.comparison.full", { defaultValue: "Completo" })}
                      </TableCell>
                      <TableCell className="text-center text-xs">
                        {t("pricing.comparison.days30", { defaultValue: "30 giorni" })}
                      </TableCell>
                      <TableCell className="text-center text-xs">
                        {t("pricing.comparison.full", { defaultValue: "Completo" })}
                      </TableCell>
                    </TableRow>

                    <TableRow className="border-border/30">
                      <TableCell className="text-xs text-muted-foreground font-medium">
                        {t("pricing.comparison.sessionPersistence", { defaultValue: "Persistenza risultati" })}
                      </TableCell>
                      <TableCell className="text-center text-xs text-muted-foreground">
                        {t("pricing.comparison.sessionOnly", { defaultValue: "Solo sessione" })}
                      </TableCell>
                      {plans.map((p) => (
                        <TableCell key={p.key} className="text-center">
                          <Check className="w-4 h-4 text-primary mx-auto" />
                        </TableCell>
                      ))}
                    </TableRow>

                    <TableRow className="border-border/30">
                      <TableCell className="text-xs text-muted-foreground font-medium">
                        {t("pricing.comparison.scope", { defaultValue: "Copertura" })}
                      </TableCell>
                      <TableCell className="text-center text-xs">
                        {t("pricing.comparison.european", { defaultValue: "Tutti i paesi UE" })}
                      </TableCell>
                      <TableCell className="text-center text-xs">
                        {t("pricing.comparison.national", { defaultValue: "Nazionale" })}
                      </TableCell>
                      <TableCell className="text-center text-xs">
                        {t("pricing.comparison.national", { defaultValue: "Nazionale" })}
                      </TableCell>
                      <TableCell className="text-center text-xs">
                        {t("pricing.comparison.european", { defaultValue: "Tutti i paesi UE" })}
                      </TableCell>
                      <TableCell className="text-center text-xs">
                        {t("pricing.comparison.european", { defaultValue: "Tutti i paesi UE" })}
                      </TableCell>
                    </TableRow>

                    <TableRow className="border-border/30">
                      <TableCell className="text-xs text-muted-foreground font-medium">
                        {t("pricing.comparison.users", { defaultValue: "Utenti inclusi" })}
                      </TableCell>
                      <TableCell className="text-center text-xs">1</TableCell>
                      <TableCell className="text-center text-xs">1</TableCell>
                      <TableCell className="text-center text-xs">3</TableCell>
                      <TableCell className="text-center text-xs">2</TableCell>
                      <TableCell className="text-center text-xs">5</TableCell>
                    </TableRow>

                    <TableRow className="border-border/30">
                      <TableCell className="text-xs text-muted-foreground font-medium">
                        {t("pricing.comparison.monitorings", { defaultValue: "Monitoraggi" })}
                      </TableCell>
                      <TableCell className="text-center text-xs">1</TableCell>
                      <TableCell className="text-center text-xs">3</TableCell>
                      <TableCell className="text-center text-xs">
                        {t("common.unlimited", { defaultValue: "Illimitati" })}
                      </TableCell>
                      <TableCell className="text-center text-xs">5</TableCell>
                      <TableCell className="text-center text-xs">
                        {t("common.unlimited", { defaultValue: "Illimitati" })}
                      </TableCell>
                    </TableRow>

                    <TableRow className="border-border/30">
                      <TableCell className="text-xs text-muted-foreground font-medium">
                        {t("pricing.comparison.alerts", { defaultValue: "Alert email" })}
                      </TableCell>
                      <TableCell className="text-center">
                        <X className="w-4 h-4 text-muted-foreground/50 mx-auto" />
                      </TableCell>
                      <TableCell className="text-center text-xs">
                        {t("pricing.comparison.standard", { defaultValue: "Standard" })}
                      </TableCell>
                      <TableCell className="text-center text-xs">
                        {t("pricing.comparison.advanced", { defaultValue: "Avanzati" })}
                      </TableCell>
                      <TableCell className="text-center text-xs">
                        {t("pricing.comparison.standard", { defaultValue: "Standard" })}
                      </TableCell>
                      <TableCell className="text-center text-xs">
                        {t("pricing.comparison.advanced", { defaultValue: "Avanzati" })}
                      </TableCell>
                    </TableRow>

                    <TableRow className="border-border/30">
                      <TableCell className="text-xs text-muted-foreground font-medium">
                        {t("pricing.comparison.export", { defaultValue: "Esportazione CSV/Excel" })}
                      </TableCell>
                      <TableCell className="text-center">
                        <X className="w-4 h-4 text-muted-foreground/50 mx-auto" />
                      </TableCell>
                      <TableCell className="text-center">
                        <X className="w-4 h-4 text-muted-foreground/50 mx-auto" />
                      </TableCell>
                      <TableCell className="text-center">
                        <Check className="w-4 h-4 text-primary mx-auto" />
                      </TableCell>
                      <TableCell className="text-center">
                        <X className="w-4 h-4 text-muted-foreground/50 mx-auto" />
                      </TableCell>
                      <TableCell className="text-center">
                        <Check className="w-4 h-4 text-primary mx-auto" />
                      </TableCell>
                    </TableRow>

                    <TableRow className="border-border/30">
                      <TableCell className="text-xs text-muted-foreground font-medium">
                        {t("pricing.comparison.support", { defaultValue: "Supporto prioritario" })}
                      </TableCell>
                      <TableCell className="text-center">
                        <X className="w-4 h-4 text-muted-foreground/50 mx-auto" />
                      </TableCell>
                      <TableCell className="text-center">
                        <X className="w-4 h-4 text-muted-foreground/50 mx-auto" />
                      </TableCell>
                      <TableCell className="text-center">
                        <Check className="w-4 h-4 text-primary mx-auto" />
                      </TableCell>
                      <TableCell className="text-center">
                        <X className="w-4 h-4 text-muted-foreground/50 mx-auto" />
                      </TableCell>
                      <TableCell className="text-center">
                        <Check className="w-4 h-4 text-primary mx-auto" />
                      </TableCell>
                    </TableRow>

                    <TableRow className="border-border/30 bg-muted/30">
                      <TableCell className="text-xs text-foreground font-semibold">
                        {t("pricing.comparison.price", { defaultValue: "Prezzo" })}
                      </TableCell>
                      <TableCell className="text-center font-bold text-muted-foreground">
                        {t("pricing.comparison.free", { defaultValue: "Free" })}
                      </TableCell>
                      {plans.map((p) => (
                        <TableCell
                          key={p.key}
                          className={`text-center font-bold ${p.highlight ? "text-primary" : "text-foreground"}`}
                        >
                          €{p.price}/{t("pricing.monthly", { defaultValue: "mese" })}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            </div>

            {/* Bottom trust / legal */}
            <div className="mt-10 text-center">
              <p className="text-xs text-muted-foreground max-w-3xl mx-auto">
                {t("pricing.disclaimer", {
                  defaultValue:
                    "TED Monitor is an independent service based on public data from TED (Tenders Electronic Daily), Supplement to the Official Journal of the European Union.",
                })}
              </p>

              <div className="mt-4 flex items-center justify-center gap-4 text-xs">
                <Link to="/privacy-policy" className="text-muted-foreground hover:text-foreground transition-colors">
                  {t("legal.footerLinks.privacy", { defaultValue: "Privacy" })}
                </Link>
                <Link to="/cookie-policy" className="text-muted-foreground hover:text-foreground transition-colors">
                  {t("legal.footerLinks.cookies", { defaultValue: "Cookie" })}
                </Link>
                <Link to="/terms-conditions" className="text-muted-foreground hover:text-foreground transition-colors">
                  {t("legal.footerLinks.terms", { defaultValue: "Termini" })}
                </Link>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
}
